import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { initialTimeline } from '../../data/initialData';

const iconOptions = ['🧠', '🔬', '⚙️', '🌐', '🎨', '🚀', '💻', '📱', '🔧', '📡', '🎯', '📚', '🎓', '📝', '🏗️'];

const emptyForm = { startDate: '', endDate: '', title: '', subtitle: '', description: '', icon: '🧠' };

const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const displayToValue = (display) => {
  if (!display) return '';
  const m = monthNames.findIndex(m => display.startsWith(m));
  if (m === -1) return '';
  const parts = display.split(' ');
  if (parts.length < 2) return '';
  const year = parts[1];
  return `${year}-${String(m + 1).padStart(2, '0')}`;
};

const valueToDisplay = (value) => {
  if (!value) return '';
  const parts = value.split('-');
  if (parts.length < 2) return value;
  const month = parseInt(parts[1], 10);
  if (month < 1 || month > 12) return value;
  return `${monthNames[month - 1]} ${parts[0]}`;
};

const TimelineManager = () => {
  const [timeline, setTimeline] = useLocalStorage('timeline', initialTimeline);
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (saved) {
      const t = setTimeout(() => setSaved(false), 2000);
      return () => clearTimeout(t);
    }
  }, [saved]);

  const openAddForm = () => {
    setFormData({ ...emptyForm });
    setEditingIndex(null);
    setIsAdding(true);
  };

  const openEditForm = (index) => {
    const item = timeline[index];
    if (!item) return;
    setFormData({
      startDate: item.startDate || item.year || '',
      endDate: item.endDate || '',
      title: item.title || '',
      subtitle: item.subtitle || '',
      description: item.description || '',
      icon: item.icon || '🧠',
    });
    setEditingIndex(index);
    setIsAdding(true);
  };

  const closeForm = () => {
    setFormData({ ...emptyForm });
    setIsAdding(false);
    setEditingIndex(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newList = [...(timeline || [])];
    if (editingIndex !== null) {
      newList[editingIndex] = { ...formData };
    } else {
      newList.push({ ...formData });
    }
    setTimeline(newList);
    setSaved(true);
    closeForm();
  };

  const handleDelete = (index) => {
    if (window.confirm('Delete this timeline entry?')) {
      setTimeline((timeline || []).filter((_, i) => i !== index));
    }
  };

  const handleMove = (index, direction) => {
    const list = timeline || [];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    const newList = [...list];
    [newList[index], newList[target]] = [newList[target], newList[index]];
    setTimeline(newList);
  };

  const items = timeline || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Professional Timeline</h2>
          <p className="text-sm text-slate-400 font-mono mt-1">{items.length} entries</p>
        </div>
        {!isAdding && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={openAddForm}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            + Add Entry
          </motion.button>
        )}
      </div>

      {saved && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="mb-4 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-300 text-sm"
        >
          Timeline saved successfully
        </motion.div>
      )}

      {isAdding && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800/80 rounded-xl p-6 mb-6 border border-slate-600 shadow-lg"
        >
          <h3 className="text-lg font-semibold text-white mb-5">
            {editingIndex !== null ? '✏️ Edit Timeline Entry' : '➕ Add Timeline Entry'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">From *</label>
                <input type="month" value={displayToValue(formData.startDate)}
                  onChange={(e) => setFormData({...formData, startDate: valueToDisplay(e.target.value)})}
                  className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm [color-scheme:dark]" required />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-300">To</label>
                  <button type="button"
                    onClick={() => setFormData({...formData, endDate: formData.endDate === 'Present' ? '' : 'Present'})}
                    className={`text-xs px-2 py-0.5 rounded border transition-all ${
                      formData.endDate === 'Present'
                        ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30'
                        : 'bg-slate-700/50 text-slate-400 border-slate-600 hover:border-slate-400'
                    }`}
                  >
                    Present
                  </button>
                </div>
                {formData.endDate === 'Present' ? (
                  <div className="w-full px-4 py-2.5 bg-slate-800/50 border border-cyan-500/30 rounded-lg text-cyan-300 text-sm flex items-center gap-2">
                    <span>📅</span> Present
                    <button type="button" onClick={() => setFormData({...formData, endDate: ''})}
                      className="ml-auto text-slate-500 hover:text-white text-xs">✕</button>
                  </div>
                ) : (
                  <input type="month" value={displayToValue(formData.endDate)}
                    onChange={(e) => setFormData({...formData, endDate: valueToDisplay(e.target.value)})}
                    className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm [color-scheme:dark]" />
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Title *</label>
              <input type="text" value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g. Machine Learning Engineer (NLP)"
                className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Subtitle (Organization)</label>
              <input type="text" value={formData.subtitle}
                onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                placeholder="e.g. Midlands State University · National Language Institute"
                className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Description *</label>
              <textarea value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                placeholder="Describe the role, key achievements, and technologies used..."
                className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm resize-none" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Icon</label>
              <div className="flex flex-wrap gap-1.5">
                {iconOptions.map(icon => (
                  <motion.button key={icon} type="button"
                    whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}
                    onClick={() => setFormData({...formData, icon})}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center text-base border-2 transition-all ${
                      formData.icon === icon ? 'border-cyan-400 bg-cyan-500/20 scale-110' : 'border-slate-600 bg-slate-700/50 hover:border-slate-400'
                    }`}>
                    {icon}
                  </motion.button>
                ))}
              </div>
            </div>
            <div className="flex space-x-3 pt-2">
              <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                className="bg-green-600 hover:bg-green-500 text-white px-6 py-2.5 rounded-lg transition-colors text-sm font-medium flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                {editingIndex !== null ? 'Update Entry' : 'Add Entry'}
              </motion.button>
              <motion.button type="button" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={closeForm}
                className="bg-slate-600 hover:bg-slate-500 text-white px-6 py-2.5 rounded-lg transition-colors text-sm font-medium">Cancel</motion.button>
            </div>
          </form>
        </motion.div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <p className="text-lg mb-2">No timeline entries yet</p>
          <p className="text-sm">Click "Add Entry" to create your first timeline entry</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => (
            <motion.div key={(item.startDate || item.year || '') + index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.04 }}
              className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50 hover:border-slate-600 transition-all group"
            >
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
                    <span className="px-2 py-0.5 text-xs font-mono text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-md font-semibold">{item.startDate || item.year || ''}{item.endDate ? ` - ${item.endDate}` : ''}</span>
                    <span className="text-lg leading-none">{item.icon}</span>
                    <h3 className="text-base font-semibold text-white truncate">{item.title}</h3>
                  </div>
                  {item.subtitle && (
                    <p className="text-xs text-cyan-400/70 font-mono mb-2 ml-1">{item.subtitle}</p>
                  )}
                  <p className="text-sm text-slate-400 leading-relaxed line-clamp-2 ml-1">{item.description}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    onClick={() => handleMove(index, -1)} disabled={index === 0}
                    className="p-1.5 text-slate-500 hover:text-white disabled:opacity-20 transition-colors opacity-0 group-hover:opacity-100" title="Move up">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7"/></svg>
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    onClick={() => handleMove(index, 1)} disabled={index === items.length - 1}
                    className="p-1.5 text-slate-500 hover:text-white disabled:opacity-20 transition-colors opacity-0 group-hover:opacity-100" title="Move down">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    onClick={() => openEditForm(index)}
                    className="p-1.5 text-blue-400 hover:text-blue-300 transition-colors" title="Edit">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    onClick={() => handleDelete(index)}
                    className="p-1.5 text-red-400 hover:text-red-300 transition-colors" title="Delete">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TimelineManager;
